import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TimeoutDialogComponent } from './timeout-dialog/timeout-dialog.component';
import { RoleTestingService } from './services/role-testing.service';
import { DataService } from './services/data.service';
import { AuthenticationService } from './services/authentication.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  EventType,
  EventMessage,
  AuthenticationResult,
  InteractionType,
} from '@azure/msal-browser';
import { HttpClient } from '@angular/common/http';
import { filter } from 'rxjs/operators';

const TIMEOUT_CHECK_INTERVAL = 1000; // 1 second
const APPROVER_STATUS_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const IT_APPROVER_TTL_KEY = 'mmaps_it_approver_status_expires_at';
const RE_APPROVER_TTL_KEY = 'mmaps_re_approver_status_expires_at';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'mmaps-web';
  timeoutInterval: any;
  private azureTokenRefreshAttempted = false;

  constructor(
    private dialog: MatDialog,
    private roleTestingService: RoleTestingService,
    private dataService: DataService, // <-- inject DataService
    private authService: AuthenticationService,
    private broadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private httpClient: HttpClient
  ) {}

  ngOnInit(): void {
    // Listen for a popup handshake message from a successful MSAL redirect
    // processed inside the popup. The popup will postMessage the auth result
    // to the opener so the main window can restore session and close the popup.
    window.addEventListener('message', this.handleAuthMessage.bind(this), false);

    this.handleMsalRedirect();
    this.captureEntryUrl();
    this.setUserInfoInSessionStorage();
    this.restoreApproverStatusFromTTL();
    this.listenForMsalTokenRenewal();
    this.checkForTimeout();
    this.setupRoleTestingConsoleHelper();
    this.checkApproverStatusAtStartup(); // <-- add this
  }

  /**
   * Process MSAL redirect responses (including those delivered to popups).
   * This ensures the popup can hand tokens back to the opener and close.
   */
  handleMsalRedirect() {
    try {
      this.msalService.instance
        .handleRedirectPromise()
        .then((result: AuthenticationResult | null) => {
          if (result && result.idToken) {
            const userInfo = JSON.stringify(result);
            sessionStorage.setItem('userInfo', userInfo);
            localStorage.setItem('userInfo', userInfo);
            console.log('[MMAPS] Processed MSAL redirect result; exchanging for Apigee token.');
            this.authService.login().subscribe(
              () => console.log('[MMAPS] Apigee token exchanged after redirect.'),
              (err) => console.warn('[MMAPS] Apigee exchange failed after redirect.', err)
            );

            // If this was opened as a popup, send a handshake message to the opener
            // with the auth result so the opener can restore session and then close
            // the popup. Use the same origin for safety.
            try {
              if (window && window.opener && window.opener !== window) {
                const payload = { type: 'mmaps:auth', userInfo };
                try {
                  window.opener.postMessage(payload, window.location.origin);
                } catch (e) {
                  // Some environments (eg. strict email browser wrappers) may block
                  // cross-window messaging; ignore and fall back to attempting to
                  // close the popup directly.
                }
                try {
                  window.close();
                } catch (e) {
                  /* ignore */
                }
              }
            } catch (e) {
              // Defensive: don't allow postMessage failures to break app flow
            }
          }
        })
        .catch((err) => console.warn('[MMAPS] Error handling MSAL redirect response.', err));
    } catch (e) {
      console.warn('[MMAPS] handleMsalRedirect failed', e);
    }
  }

  /**
   * Handle a postMessage sent by a successful login processed inside a popup.
   * The popup sends { type: 'mmaps:auth', userInfo } where userInfo is the
   * stringified AuthenticationResult. We restore it to session/local storage
   * and exchange with Apigee (authService.login()). If an entryURL was saved
   * prior to redirect, navigate there.
   */
  handleAuthMessage(event: MessageEvent) {
    try {
      if (!event || event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== 'mmaps:auth' || !data.userInfo) return;

      try {
        sessionStorage.setItem('userInfo', data.userInfo);
        localStorage.setItem('userInfo', data.userInfo);
      } catch (e) {
        /* ignore storage errors */
      }

      // Exchange with Apigee to obtain the application token
      this.authService.login().subscribe(
        () => {
          console.log('[MMAPS] Apigee token exchanged after popup handshake.');
          const savedEntry = sessionStorage.getItem('entryURL') || localStorage.getItem('entryURL');
          // Only navigate back to a saved entry URL if it looks like a deep-link to a form
          // (we expect '?guid' or '&guid' or 'pId=' in the query). This avoids navigating
          // to a stale saved URL when the user simply visited the site root.
          if (
            savedEntry &&
            (savedEntry.indexOf('?guid') >= 0 ||
              savedEntry.indexOf('&guid') >= 0 ||
              savedEntry.indexOf('pId=') >= 0)
          ) {
            try {
              // Clear stored entryURL so we don't redirect repeatedly
              sessionStorage.removeItem('entryURL');
              localStorage.removeItem('entryURL');
            } catch (e) {
              /* ignore */
            }
            try {
              window.location.href = savedEntry;
            } catch (e) {
              /* ignore */
            }
          } else {
            // If savedEntry exists but doesn't appear to be a deep-link, clear it to avoid
            // unexpected navigation on future logins.
            try {
              sessionStorage.removeItem('entryURL');
              localStorage.removeItem('entryURL');
            } catch (e) {
              /* ignore */
            }
          }
        },
        (err) => console.warn('[MMAPS] Apigee exchange failed after popup handshake.', err)
      );
    } catch (e) {
      console.warn('[MMAPS] handleAuthMessage failed', e);
    }
  }

  /**
   * If the user arrived via a direct email link containing ?guid, save the full URL
   * to localStorage so it survives a full-page MSAL redirect to Azure AD.
   */
  captureEntryUrl() {
    const currentUrl = window.location.href;
    if (currentUrl.includes('?guid') || currentUrl.includes('&guid')) {
      localStorage.setItem('entryURL', currentUrl);
    }
  }

  setUserInfoInSessionStorage() {
    const userData = JSON.parse(localStorage.getItem('userInfo'));
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const fullTokenPayload = JSON.parse(localStorage.getItem('fullTokenPayload'));

    sessionStorage.setItem('userInfo', JSON.stringify(userData));
    sessionStorage.setItem('currentUser', JSON.stringify(currentUser));
    sessionStorage.setItem('fullTokenPayload', JSON.stringify(fullTokenPayload));

    // Restore entryURL from localStorage to sessionStorage (it may have been saved
    // before a MSAL full-page redirect wiped sessionStorage).
    const savedEntryUrl = localStorage.getItem('entryURL');
    if (savedEntryUrl) {
      sessionStorage.setItem('entryURL', savedEntryUrl);
    }
  }

  private setApproverStatusWithTTL(type: 'it' | 're') {
    const expiresAt = (Date.now() + APPROVER_STATUS_TTL_MS).toString();
    if (type === 'it') {
      sessionStorage.setItem('mmaps_it_approver_status', 'true');
      localStorage.setItem(IT_APPROVER_TTL_KEY, expiresAt);
      return;
    }
    sessionStorage.setItem('mmaps_re_approver_status', 'true');
    localStorage.setItem(RE_APPROVER_TTL_KEY, expiresAt);
  }

  private isTTLValid(key: string): boolean {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      localStorage.removeItem(key);
      return false;
    }
    return true;
  }

  private restoreApproverStatusFromTTL() {
    if (this.isTTLValid(IT_APPROVER_TTL_KEY)) {
      sessionStorage.setItem('mmaps_it_approver_status', 'true');
    }
    if (this.isTTLValid(RE_APPROVER_TTL_KEY)) {
      sessionStorage.setItem('mmaps_re_approver_status', 'true');
    }
  }

  listenForMsalTokenRenewal() {
    this.broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
            (msg.interactionType === InteractionType.Silent ||
              msg.interactionType === InteractionType.Redirect)
        )
      )
      .subscribe((msg: EventMessage) => {
        const result = msg.payload as AuthenticationResult;
        if (!result || !result.idToken) {
          return;
        }

        const existingUserInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        const existingIdToken = existingUserInfo && existingUserInfo.idToken;

        // Always keep userInfo fresh so API calls use a valid idToken
        const userInfo = JSON.stringify(result);
        sessionStorage.setItem('userInfo', userInfo);
        localStorage.setItem('userInfo', userInfo);

        // Only re-exchange for an Apigee token when MSAL issued a genuinely new idToken
        // (not every intercepted request which reuses the cached token)
        if (result.idToken !== existingIdToken) {
          console.log(
            '[MMAPS] Azure AD token renewed via MSAL broadcast, refreshing Apigee token.'
          );
          // A new idToken is confirmed — safe to allow the next Graph-triggered
          // silent refresh cycle if the token nears expiry again in future.
          this.azureTokenRefreshAttempted = false;
          this.authService.login().subscribe(
            () => console.log('[MMAPS] Apigee token refreshed after MSAL renewal.'),
            (err) => console.warn('[MMAPS] Apigee token refresh failed after MSAL renewal.', err)
          );
        }
      });
  }

  checkForTimeout() {
    this.timeoutInterval = setInterval(() => {
      const tokenData = JSON.parse(sessionStorage.getItem('fullTokenPayload'));
      if (!tokenData) return;

      const tokenIssueTime = tokenData.issued_at;
      const tokenExpiresIn = Number(tokenData.expires_in) * 1000; // seconds * 1000 = milliseconds

      const tokenExpiryTime = Number(tokenIssueTime) + tokenExpiresIn;
      const currentTime = Date.now();

      const fiveMins = 5 * 60 * 1000;
      const tenMins = 10 * 60 * 1000;

      // Proactively refresh Azure AD token at 10-minute mark by hitting the one
      // URL covered by MsalInterceptor — this triggers acquireTokenSilent which
      // fires ACQUIRE_TOKEN_SUCCESS and our listenForMsalTokenRenewal() handler
      // syncs userInfo and re-exchanges for a new Apigee token.
      if (!this.azureTokenRefreshAttempted) {
        const userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        const azureExp = userInfo && userInfo.idTokenClaims && userInfo.idTokenClaims.exp;
        if (azureExp && azureExp * 1000 - currentTime <= tenMins) {
          this.azureTokenRefreshAttempted = true;
          console.log(
            '[MMAPS] Azure AD token nearing expiry, triggering silent refresh via Graph.'
          );
          // Do NOT reset azureTokenRefreshAttempted here — the Graph call returns
          // quickly but the MSAL acquireTokenSilent it triggers is still in flight.
          // Resetting here causes the log to repeat every second until the new
          // idToken arrives. The flag is cleared in listenForMsalTokenRenewal once
          // a genuinely new idToken has been confirmed in sessionStorage.
          this.httpClient.get('https://graph.microsoft.com/v1.0/me').subscribe();
        }
      }

      const remainingMs = tokenExpiryTime - currentTime;
      const remainingTimeInSeconds = Math.round(remainingMs / 1000);

      // Token is already expired — silently try to get a new Apigee token
      // rather than opening the dialog with a nonsensical negative countdown.
      if (remainingMs <= 0) {
        console.log('[MMAPS] Apigee token already expired, attempting silent refresh.');
        clearInterval(this.timeoutInterval);
        this.authService.login().subscribe(
          () => {
            console.log('[MMAPS] Apigee token silently refreshed after expiry.');
            this.checkForTimeout();
          },
          (err) => console.warn('[MMAPS] Silent Apigee refresh after expiry failed.', err)
        );
        return;
      }

      const showDialog = remainingMs <= fiveMins;

      if (showDialog) {
        clearInterval(this.timeoutInterval);
        this.dialog
          .open(TimeoutDialogComponent, {
            width: '800px',
            disableClose: true,
            data: {
              remainingTimeInSeconds,
            },
          })
          .afterClosed()
          .subscribe(() => {
            this.checkForTimeout();
          });
      }
    }, TIMEOUT_CHECK_INTERVAL);
  }

  /**
   * Setup browser console helper for role testing (development only)
   */
  private setupRoleTestingConsoleHelper(): void {
    (window as any).roleTest = this.roleTestingService.getConsoleHelpers();

    if (this.roleTestingService.isRoleTestingAvailable()) {
      console.log('🧪 MMAPS Role Testing Available!');
      console.log('Type "roleTest.help()" in the browser console for usage instructions.');
      this.roleTestingService.logTestingStatus();
    } else {
      console.log('🚫 Role Testing is not available in this environment.');
    }
  }

  checkApproverStatusAtStartup() {
    const userData = JSON.parse(sessionStorage.getItem('userInfo'));
    // Use role testing override if active
    let userRoles = (userData && userData.idTokenClaims && userData.idTokenClaims.roles) || [];
    userRoles = this.roleTestingService.applyRoleTestingOverride(userRoles);
    const userEmail =
      userData && ((userData.account && userData.account.username) || userData.preferred_username)
        ? (
            (userData.account && userData.account.username) ||
            userData.preferred_username
          ).toLowerCase()
        : ''; // Also get display name in "last, first" format for Real Estate assigned_to matching
    let displayName = '';
    if (userData && userData.account && userData.account.name) {
      displayName = userData.account.name.trim().toLowerCase();
    }
    //console.log('[MMAPS] Dynamic approver search using userEmail:', userEmail, '| displayName:', displayName);

    if (!userEmail) {
      // No user email, cannot evaluate dynamic approver assignment
      console.log('[MMAPS] Skipping dynamic approver search: no email.');
      return;
    }

    // Check IT forms
    console.log('[MMAPS] Searching IT forms for dynamic approver access...');
    this.dataService.getItSparData({}, 'reviews').subscribe((itRes: any) => {
      const itForms = itRes.body || [];
      if (!Array.isArray(itForms) || itForms.length === 0) {
        console.log('[MMAPS] No IT forms found for dynamic approver search.');
      }
      let isITApprover = false;
      const emailAlias = userEmail.includes('@') ? userEmail.split('@')[0] : userEmail;
      const matchEmail = (raw: any) => {
        if (!raw) return false;
        const norm = String(raw).toLowerCase();
        return norm === userEmail || norm === emailAlias;
      };
      for (let i = 0; i < itForms.length; i++) {
        const form = itForms[i];
        // Uncomment for troubleshooting: log assigned_to values for IT forms
        // console.log(`[MMAPS] IT Project #${i + 1} assigned_to values:`, form.assigned_to);
        if (
          (form.itSponsor && matchEmail(form.itSponsor.mail || form.itSponsor.mailNickname)) ||
          (form.approvers &&
            form.approvers.some((a: any) => matchEmail(a.email || a.mailNickname || a.mail))) ||
          (form.jointOwnerCoordinator && matchEmail(form.jointOwnerCoordinator.mail)) ||
          (form.currentAssignment &&
            matchEmail(form.currentAssignment.mail || form.currentAssignment.mailNickname)) ||
          (form.currentApprover &&
            matchEmail(form.currentApprover.mail || form.currentApprover.mailNickname))
        ) {
          isITApprover = true;
          break;
        }
      }
      if (isITApprover) {
        console.log('[MMAPS] User is a dynamic IT approver. IT dashboard access granted.');
        this.setApproverStatusWithTTL('it');
      } else {
        if (sessionStorage.getItem('mmaps_it_approver_status') !== 'true') {
          sessionStorage.removeItem('mmaps_it_approver_status');
        }
        if (Array.isArray(itForms) && itForms.length > 0) {
          console.log('[MMAPS] User is NOT a dynamic IT approver.');
        }
      }

      // Check Real Estate forms regardless
      console.log('[MMAPS] Searching Real Estate forms for dynamic approver access...');
      this.dataService.getRREData().subscribe((reRes: any) => {
        const projects = reRes.projects || reRes || [];
        if (!Array.isArray(projects) || projects.length === 0) {
          console.log('[MMAPS] No Real Estate forms found for dynamic approver search.');
        }
        let foundREApprover = false;
        for (let idx = 0; idx < projects.length; idx++) {
          const project = projects[idx];
          const assignedTo = project.assigned_to;
          let assignedValues: string[] = [];
          if (Array.isArray(assignedTo)) {
            assignedValues = assignedTo.map((a: any) =>
              typeof a === 'string' ? a.trim().toLowerCase() : ''
            );
          } else if (typeof assignedTo === 'string') {
            assignedValues = [assignedTo.trim().toLowerCase()];
          }
          // Uncomment for troubleshooting: log assigned_to values for Real Estate forms
          // console.log(`[MMAPS] Real Estate Project #${idx + 1} assigned_to values:`, assignedValues);
          if (
            assignedValues.includes(userEmail) ||
            (displayName && assignedValues.includes(displayName))
          ) {
            foundREApprover = true;
            break;
          }
        }
        if (foundREApprover) {
          console.log(
            '[MMAPS] User is a dynamic Real Estate approver (assigned_to). Real Estate dashboard access granted.'
          );
          this.setApproverStatusWithTTL('re');
        } else {
          if (sessionStorage.getItem('mmaps_re_approver_status') !== 'true') {
            sessionStorage.removeItem('mmaps_re_approver_status');
          }
          if (Array.isArray(projects) && projects.length > 0) {
            console.log('[MMAPS] User is NOT a dynamic Real Estate approver.');
          }
        }
      });
    });
  }
}
